import { useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Alert,
  Snackbar,
  Collapse,
} from '@mui/material';
import { Check, Map as MapIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setCurrentBasemap } from '../store/slices/mapSlice';
import { BASEMAP_CONFIGS, type BasemapType } from '../config/basemaps';

interface BasemapSelectorProps {
  /**
   * Optional callback when basemap is selected
   */
  onBasemapChange?: (basemapType: BasemapType) => void;
  /**
   * Optional className for custom styling
   */
  className?: string;
  /**
   * Display mode: 'compact' for floating control, 'full' for full dialog
   */
  mode?: 'compact' | 'full';
}

/**
 * BasemapSelector Component
 *
 * Displays available basemap options and allows users to switch between them.
 * Integrates with Redux to manage the current basemap selection.
 *
 * Features:
 * - Two display modes: compact (floating panel) and full (grid)
 * - Displays all available basemap options with name and description
 * - Highlights the currently selected basemap
 * - Dispatches Redux action to update basemap
 * - Shows error messages if basemap fails to load
 * - Responsive grid layout (full mode)
 * - Accessible keyboard navigation
 */
const BasemapSelector: React.FC<BasemapSelectorProps> = ({
  onBasemapChange,
  className = '',
  mode = 'full',
}) => {
  const dispatch = useAppDispatch();
  const currentBasemap = useAppSelector((state) => state.map.currentBasemap);
  const basemapError = useAppSelector((state) => state.map.basemapError);
  const [showError, setShowError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Show error snackbar when basemap error occurs
  const handleCloseError = () => {
    setShowError(false);
  };

  // Handle basemap selection
  const handleBasemapSelect = (basemapType: BasemapType) => {
    // Don't do anything if already selected
    if (basemapType === currentBasemap) {
      if (mode === 'compact') {
        setIsExpanded(false);
      }
      return;
    }

    // Dispatch Redux action to update basemap
    dispatch(setCurrentBasemap(basemapType));

    // Call optional callback
    if (onBasemapChange) {
      onBasemapChange(basemapType);
    }

    // Collapse in compact mode after selection
    if (mode === 'compact') {
      setIsExpanded(false);
    }
  };

  // Toggle expanded state
  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Show error snackbar when error changes
  if (basemapError && !showError) {
    setShowError(true);
  }

  // Get current basemap config
  const currentBasemapConfig = BASEMAP_CONFIGS[currentBasemap];

  // Compact mode (floating control)
  if (mode === 'compact') {
    return (
      <Box className={className} data-testid="basemap-selector">
        <Card
          elevation={3}
          sx={{
            minWidth: 200,
            maxWidth: 280,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          {/* Header with current basemap */}
          <CardActionArea
            onClick={handleToggleExpanded}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapIcon color="primary" />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {currentBasemapConfig.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Basemap
                </Typography>
              </Box>
            </Box>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </CardActionArea>

          {/* Expandable basemap options */}
          <Collapse in={isExpanded}>
            <Box sx={{ p: 1, pt: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 1, mb: 1, display: 'block' }}
              >
                Choose Basemap:
              </Typography>

              {Object.values(BASEMAP_CONFIGS).map((config) => {
                const isSelected = config.id === currentBasemap;

                return (
                  <Card
                    key={config.id}
                    variant="outlined"
                    sx={{
                      mb: 1,
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 1,
                      },
                      '&:last-child': { mb: 0 },
                    }}
                    data-testid={`basemap-option-${config.id}`}
                  >
                    <CardActionArea onClick={() => handleBasemapSelect(config.id)} sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Selection indicator */}
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            border: 2,
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: isSelected ? 'primary.main' : 'transparent',
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          {isSelected && <Check sx={{ color: 'white', fontSize: 14 }} />}
                        </Box>

                        {/* Basemap info */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 600 : 500,
                              color: isSelected ? 'primary.main' : 'text.primary',
                            }}
                          >
                            {config.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', lineHeight: 1.2 }}
                          >
                            {config.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          </Collapse>
        </Card>

        {/* Error Snackbar */}
        <Snackbar
          open={showError}
          autoHideDuration={6000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseError}
            severity="error"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {basemapError}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Full mode (original grid layout)
  return (
    <Box className={className} data-testid="basemap-selector">
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Select Basemap
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}
      >
        {Object.values(BASEMAP_CONFIGS).map((config) => {
          const isSelected = config.id === currentBasemap;

          return (
            <Box key={config.id}>
              <Card
                sx={{
                  position: 'relative',
                  border: isSelected ? 2 : 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                }}
                data-testid={`basemap-option-${config.id}`}
              >
                <CardActionArea
                  onClick={() => handleBasemapSelect(config.id)}
                  aria-label={`Select ${config.name} basemap`}
                  aria-pressed={isSelected}
                >
                  <CardContent>
                    {/* Selected indicator */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        data-testid={`basemap-selected-${config.id}`}
                      >
                        <Check sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                    )}

                    {/* Basemap name */}
                    <Typography
                      variant="h6"
                      component="div"
                      gutterBottom
                      sx={{
                        fontWeight: isSelected ? 600 : 500,
                        pr: isSelected ? 5 : 0, // Add padding for check icon
                      }}
                    >
                      {config.name}
                    </Typography>

                    {/* Basemap description */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {config.description}
                    </Typography>

                    {/* Attribution */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        fontSize: '0.7rem',
                        lineHeight: 1.3,
                      }}
                    >
                      {config.attribution}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" variant="filled" sx={{ width: '100%' }}>
          {basemapError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BasemapSelector;
